import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import CustomOffers from './CustomOffer';
import { CallInformationDetailed } from '../CallInformation/CallInformationDetailed';
import OverviewDashboard from '../PMetricsPage/OverviewDashboard.js';
import ChurnDashboard from '../PMetricsPage/ChurnDashboard.js';
import ChurnDetail from '../PMetricsPage/ChurnDetail.js';
import IntercomTrends from '../PMetricsPage/IntercomTrends.js';
import CallInsightsDashboard from '../PMetricsPage/CallInsightsDashboard';
import CallLogDetail from '../PMetricsPage/CallLogDetail';
import AdminPhoneNumbers from './screens/AdminPhoneNumbers.js';
import FeatureAnalyticsDashboard from './FeatureAnalytics/FeatureAnalyticsDashboard.js';

interface LoginFormData {
	username: string;
	password: string;
}

interface AccessUserFormData {
	username: string;
	userType: 'Client' | 'Reseller';
}

interface CloneUserFormData {
	username: string;
}

interface CloneSuccessData {
	clonedUsername: string;
	customerId: string;
	subscriptionId: string;
	agentsCount: number;
	expiresAt: string;
}

const baseUrl = import.meta.env.VITE_APP_API_BASE_URL;

// Reusable loading spinner component
const LoadingSpinner = ({ text }: { text: string }) => (
	<>
		<svg
			className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
		>
			<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
			<path
				className="opacity-75"
				fill="currentColor"
				d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
			/>
		</svg>
		{text}
	</>
);

// Custom PMetrics wrapper component that doesn't rely on React Router
const PMetricsWrapper = ({ onBack }: { onBack: () => void }) => {
	const [currentPMetricsView, setCurrentPMetricsView] = useState<
		'overview' | 'churn' | 'churnDetail' | 'callInsights' | 'callDetail' | 'intercomTrends'
	>('overview');

	const [selectedIdentifier, setSelectedIdentifier] = useState<string | null>(null);
	const [selectedCallSid, setSelectedCallSid] = useState<string | null>(null);

	const handleBackToChurn = () => {
		setSelectedIdentifier(null);
		setCurrentPMetricsView('churn');
	};

	const handleViewChurnDetail = (identifier: string) => {
		setSelectedIdentifier(identifier);
		setCurrentPMetricsView('churnDetail');
	};

	const handleViewCallDetail = (callSid: string) => {
		setSelectedCallSid(callSid);
		setCurrentPMetricsView('callDetail');
	};

	const handleBackToCallInsights = () => {
		setSelectedCallSid(null);
		setCurrentPMetricsView('callInsights');
	};

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header with navigation */}
			<div className="bg-white shadow-sm border-b">
				<div className="max-w-6xl mx-auto px-6 py-4">
					<div className="flex justify-between items-center">
						<div className="flex items-center space-x-6">
							<button
								onClick={onBack}
								className="text-indigo-600 hover:text-indigo-800 font-medium"
							>
								← Back to Admin Dashboard
							</button>
							<h1 className="text-2xl font-bold text-gray-900">PMetrics Dashboard</h1>
						</div>
						<div className="flex space-x-4">
							<button
								onClick={() => setCurrentPMetricsView('overview')}
								className={`px-4 py-2 text-sm font-medium rounded-md ${
									currentPMetricsView === 'overview'
										? 'bg-indigo-600 text-white'
										: 'bg-gray-200 text-gray-700 hover:bg-gray-300'
								}`}
							>
								Overview
							</button>
							<button
								onClick={() => setCurrentPMetricsView('churn')}
								className={`px-4 py-2 text-sm font-medium rounded-md ${
									currentPMetricsView === 'churn'
										? 'bg-indigo-600 text-white'
										: 'bg-gray-200 text-gray-700 hover:bg-gray-300'
								}`}
							>
								Churn Analyzer
							</button>
							<button
								onClick={() => setCurrentPMetricsView('callInsights')}
								className={`px-4 py-2 text-sm font-medium rounded-md ${
									currentPMetricsView === 'callInsights'
										? 'bg-indigo-600 text-white'
										: 'bg-gray-200 text-gray-700 hover:bg-gray-300'
								}`}
							>
								Call Insights
							</button>
							<button
								onClick={() => setCurrentPMetricsView('intercomTrends')}
								className={`px-4 py-2 text-sm font-medium rounded-md ${
									currentPMetricsView === 'intercomTrends'
										? 'bg-indigo-600 text-white'
										: 'bg-gray-200 text-gray-700 hover:bg-gray-300'
								}`}
							>
								Intercom Trends
							</button>
						</div>
					</div>
				</div>
			</div>

			{/* Content */}
			<div className="max-w-6xl mx-auto p-6">
				<div style={{ display: currentPMetricsView === 'overview' ? 'block' : 'none' }}>
					<OverviewDashboard />
				</div>
				<div style={{ display: currentPMetricsView === 'churn' ? 'block' : 'none' }}>
					<ChurnDashboard onViewDetail={handleViewChurnDetail} />+{' '}
				</div>
				{currentPMetricsView === 'churnDetail' && (
					<div>
						<div className="mb-4">
							<button
								onClick={handleBackToChurn}
								className="text-indigo-600 hover:text-indigo-800 font-medium"
							>
								← Back to Churn Analyzer
							</button>
						</div>
						<ChurnDetail identifier={selectedIdentifier} />
					</div>
				)}
				<div style={{ display: currentPMetricsView === 'callInsights' ? 'block' : 'none' }}>
					<CallInsightsDashboard onViewDetail={handleViewCallDetail} />
				</div>
				{currentPMetricsView === 'callDetail' && selectedCallSid && (
					<div>
						<div className="mb-4">
							<button
								onClick={handleBackToCallInsights}
								className="text-indigo-600 hover:text-indigo-800 font-medium"
							>
								← Back to Call Insights
							</button>
						</div>
						<CallLogDetail callSid={selectedCallSid} />
					</div>
				)}
				{currentPMetricsView === 'intercomTrends' && <IntercomTrends />}
			</div>
		</div>
	);
};

const AdminDashboard = () => {
	const navigate = useNavigate();
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [currentView, setCurrentView] = useState<
		| 'main'
		| 'customOffers'
		| 'callInformation'
		| 'pMetrics'
		| 'prStats'
		| 'phoneNumbers'
		| 'featureAnalytics'
	>('main');
	const [loginForm, setLoginForm] = useState<LoginFormData>({
		username: '',
		password: '',
	});
	const [accessUserForm, setAccessUserForm] = useState<AccessUserFormData>({
		username: '',
		userType: 'Client',
	});
	const [cloneUserForm, setCloneUserForm] = useState<CloneUserFormData>({
		username: '',
	});
	const [cloneSuccessData, setCloneSuccessData] = useState<CloneSuccessData | null>(null);
	const [showCloneSuccess, setShowCloneSuccess] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string>('');
	const [successMessage, setSuccessMessage] = useState<string>('');
	const [isLoginLoading, setIsLoginLoading] = useState(false);
	const [isAccessUserLoading, setIsAccessUserLoading] = useState(false);
	const [isCloneUserLoading, setIsCloneUserLoading] = useState(false);
	const [grafanaCreds, setGrafanaCreds] = useState({ user: '', password: '' });
	const [isGrafanaLoading, setIsGrafanaLoading] = useState(true);
	const [copiedItem, setCopiedItem] = useState<string | null>(null);

	useEffect(() => {
		checkAdminAuth();
		fetchGrafanaCreds();
	}, []);

	const fetchGrafanaCreds = async () => {
		const token = LocalStorageUtility.getLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
		if (!token) {
			setIsGrafanaLoading(false);
			return;
		}

		try {
			setIsGrafanaLoading(true);
			const response = await axios.get(`${baseUrl}/api/admin/grafana-creds`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (response.data.success) {
				setGrafanaCreds(response.data.data);
			}
		} catch {
			setErrorMessage('Could not load Grafana credentials.');
		} finally {
			setIsGrafanaLoading(false);
		}
	};

	const checkAdminAuth = async () => {
		const token = LocalStorageUtility.getLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
		if (!token) {
			setIsAuthenticated(false);
			return;
		}

		try {
			const response = await axios.get(`${baseUrl}/api/admin/login-check`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (response.data.success) {
				setIsAuthenticated(true);
			} else {
				LocalStorageUtility.removeLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
				setIsAuthenticated(false);
			}
		} catch {
			LocalStorageUtility.removeLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
			setIsAuthenticated(false);
		}
	};

	const handleLogin = async (e: React.FormEvent) => {
		e.preventDefault();
		setErrorMessage('');
		setSuccessMessage('');

		try {
			setIsLoginLoading(true);
			const response = await axios.post(`${baseUrl}/api/admin/login`, loginForm);
			if (response.data.success) {
				LocalStorageUtility.setLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN, response.data.token);
				setIsAuthenticated(true);
				setSuccessMessage('Login successful!');
			}
		} catch (error: any) {
			console.error('Login failed:', error);
			setErrorMessage(
				error.response?.data?.message || 'Login failed. Please check your credentials.',
			);
		} finally {
			setIsLoginLoading(false);
		}
	};

	const handleAccessUser = async (e: React.FormEvent) => {
		e.preventDefault();
		setErrorMessage('');
		setSuccessMessage('');

		const token = LocalStorageUtility.getLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
		if (!token) {
			setErrorMessage('No admin token found. Please login again.');
			return;
		}

		try {
			setIsAccessUserLoading(true);
			const response = await axios.post(
				`${baseUrl}/api/admin/access-user`,
				{
					username: accessUserForm.username,
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (response.data.success) {
				setSuccessMessage(`Successfully accessed user: ${accessUserForm.username}`);
				if (accessUserForm.userType === 'Client') {
					LocalStorageUtility.setLocalData(LocalStorageKeys.AUTH_TOKEN, response.data.token);
					navigate('/');
				} else {
					LocalStorageUtility.setLocalData(
						LocalStorageKeys.RESELLER_AUTH_TOKEN,
						response.data.token,
					);
					navigate('/reseller_portal');
				}
			}
		} catch (error: any) {
			console.error('Access user failed:', error);
			setErrorMessage(
				error.response?.data?.message || 'Failed to access user. Please check the username.',
			);
		} finally {
			setIsAccessUserLoading(false);
		}
	};

	const handleCloneUser = async (e: React.FormEvent) => {
		e.preventDefault();
		setErrorMessage('');
		setSuccessMessage('');

		const token = LocalStorageUtility.getLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
		if (!token) {
			setErrorMessage('No admin token found. Please login again.');
			return;
		}

		try {
			setIsCloneUserLoading(true);
			const response = await axios.post(
				`${baseUrl}/api/admin/clone-user`,
				{
					username: cloneUserForm.username,
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);

			if (response.data.success) {
				setCloneSuccessData(response.data.data);
				setShowCloneSuccess(true);
				setCloneUserForm({ username: '' }); // Reset form
				setSuccessMessage(`Successfully cloned user: ${cloneUserForm.username}`);
			}
		} catch (error: any) {
			console.error('Clone user failed:', error);
			setErrorMessage(
				error.response?.data?.message ||
					'Failed to clone user. Please check the username and try again.',
			);
		} finally {
			setIsCloneUserLoading(false);
		}
	};

	const handleCopy = (textToCopy: string, itemName: string) => {
		if (!textToCopy) {
			return;
		}

		navigator.clipboard.writeText(textToCopy);
		setCopiedItem(itemName);
		setTimeout(() => {
			setCopiedItem(null);
		}, 1000);
	};

	const handleLogout = () => {
		LocalStorageUtility.removeLocalData(LocalStorageKeys.ADMIN_AUTH_TOKEN);
		setIsAuthenticated(false);
		setCurrentView('main');
	};

	if (!isAuthenticated) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<form
					onSubmit={handleLogin}
					className="w-full max-w-md space-y-4 p-6 bg-white rounded-lg shadow-md"
				>
					<h2 className="text-2xl font-bold text-center mb-6">Admin Login</h2>
					<div>
						<label className="block text-sm font-medium text-gray-700">Username</label>
						<input
							type="text"
							value={loginForm.username}
							onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
							className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
							required
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700">Password</label>
						<input
							type="password"
							value={loginForm.password}
							onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
							className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
							required
						/>
					</div>
					<button
						type="submit"
						disabled={isLoginLoading}
						className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
					>
						{isLoginLoading ? <LoadingSpinner text="Logging in..." /> : 'Login'}
					</button>
				</form>
			</div>
		);
	}

	// Render different views based on currentView state
	if (currentView === 'customOffers') {
		return (
			<div className="min-h-screen bg-gray-50">
				{/* Header with navigation */}
				<div className="bg-white shadow-sm border-b">
					<div className="max-w-6xl mx-auto px-6 py-4">
						<div className="flex justify-between items-center">
							<div className="flex items-center space-x-6">
								<button
									onClick={() => setCurrentView('main')}
									className="text-indigo-600 hover:text-indigo-800 font-medium"
								>
									← Back to Admin Dashboard
								</button>
								<h1 className="text-2xl font-bold text-gray-900">Custom Offers</h1>
							</div>
							<button
								onClick={handleLogout}
								className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
							>
								Logout
							</button>
						</div>
					</div>
				</div>

				{/* Custom Offers Component */}
				<CustomOffers />
			</div>
		);
	}

	if (currentView === 'callInformation') {
		return (
			<div className="min-h-screen bg-gray-50">
				{/* Header with navigation */}
				<div className="bg-white shadow-sm border-b">
					<div className="max-w-6xl mx-auto px-6 py-4">
						<div className="flex justify-between items-center">
							<div className="flex items-center space-x-6">
								<button
									onClick={() => setCurrentView('main')}
									className="text-indigo-600 hover:text-indigo-800 font-medium"
								>
									← Back to Admin Dashboard
								</button>
								<h1 className="text-2xl font-bold text-gray-900">Call Information</h1>
							</div>
							<button
								onClick={handleLogout}
								className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
							>
								Logout
							</button>
						</div>
					</div>
				</div>

				{/* Call Information Component */}
				<CallInformationDetailed />
			</div>
		);
	}

	if (currentView === 'pMetrics') {
		return <PMetricsWrapper onBack={() => setCurrentView('main')} />;
	}

	if (currentView === 'phoneNumbers') {
		return <AdminPhoneNumbers onBack={() => setCurrentView('main')} />;
	}

	// if (currentView === 'prStats') {
	// 	return (
	// 		<div className="min-h-screen bg-gray-50">
	// 			{/* Header with navigation */}
	// 			<div className="bg-white shadow-sm border-b">
	// 				<div className="max-w-6xl mx-auto px-6 py-4">
	// 					<div className="flex justify-between items-center">
	// 						<div className="flex items-center space-x-6">
	// 							<button
	// 								onClick={() => setCurrentView('main')}
	// 								className="text-indigo-600 hover:text-indigo-800 font-medium"
	// 							>
	// 								← Back to Admin Dashboard
	// 							</button>
	// 							<h1 className="text-2xl font-bold text-gray-900">PR Stats</h1>
	// 						</div>
	// 						<button
	// 							onClick={handleLogout}
	// 							className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
	// 						>
	// 							Logout
	// 						</button>
	// 					</div>
	// 				</div>
	// 			</div>

	// 			{/* PR Stats Component */}
	// 			<PRStatsPage />
	// 		</div>
	// 	);
	// }

	if (currentView === 'featureAnalytics') {
		return <FeatureAnalyticsDashboard onBack={() => setCurrentView('main')} />;
	}

	// Main admin dashboard view
	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<div className="bg-white shadow-sm border-b">
				<div className="max-w-6xl mx-auto px-6 py-4">
					<div className="flex justify-between items-center">
						<h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
						<button
							onClick={handleLogout}
							className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
						>
							Logout
						</button>
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div className="max-w-6xl mx-auto p-6">
				{/* Error Message */}
				{errorMessage && (
					<div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
						<div className="flex">
							<div className="flex-shrink-0">
								<svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
									<path
										fillRule="evenodd"
										d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
										clipRule="evenodd"
									/>
								</svg>
							</div>
							<div className="ml-3">
								<p className="text-sm text-red-800">{errorMessage}</p>
							</div>
							<div className="ml-auto pl-3">
								<div className="-mx-1.5 -my-1.5">
									<button
										onClick={() => setErrorMessage('')}
										className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-600"
									>
										<span className="sr-only">Dismiss</span>
										<svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
											<path
												fillRule="evenodd"
												d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
												clipRule="evenodd"
											/>
										</svg>
									</button>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Success Message */}
				{successMessage && (
					<div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
						<div className="flex">
							<div className="flex-shrink-0">
								<svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
									<path
										fillRule="evenodd"
										d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
										clipRule="evenodd"
									/>
								</svg>
							</div>
							<div className="ml-3">
								<p className="text-sm text-green-800">{successMessage}</p>
							</div>
							<div className="ml-auto pl-3">
								<div className="-mx-1.5 -my-1.5">
									<button
										onClick={() => setSuccessMessage('')}
										className="inline-flex bg-green-50 rounded-md p-1.5 text-green-500 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600"
									>
										<span className="sr-only">Dismiss</span>
										<svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
											<path
												fillRule="evenodd"
												d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
												clipRule="evenodd"
											/>
										</svg>
									</button>
								</div>
							</div>
						</div>
					</div>
				)}

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
					{/* Access User Card */}
					<div className="bg-white rounded-lg shadow-md p-6">
						<h2 className="text-xl font-semibold mb-4">Access User Account</h2>
						<form onSubmit={handleAccessUser} className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
								<input
									type="text"
									value={accessUserForm.username}
									onChange={(e) =>
										setAccessUserForm({ ...accessUserForm, username: e.target.value })
									}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
									required
								/>
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">User Type</label>
								<select
									value={accessUserForm.userType}
									onChange={(e) =>
										setAccessUserForm({
											...accessUserForm,
											userType: e.target.value as 'Client' | 'Reseller',
										})
									}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
								>
									<option value="Client">Client</option>
									<option value="Reseller">Reseller</option>
								</select>
							</div>
							<button
								type="submit"
								disabled={isAccessUserLoading}
								className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isAccessUserLoading ? <LoadingSpinner text="Accessing..." /> : 'Access User'}
							</button>
						</form>
					</div>

					{/* Custom Offers Card */}
					<div className="bg-white rounded-lg shadow-md p-6">
						<h2 className="text-xl font-semibold mb-4">Custom Offers</h2>
						<p className="text-gray-600 mb-4">
							Create and manage custom offer URLs for your clients.
						</p>
						<button
							onClick={() => setCurrentView('customOffers')}
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
						>
							Manage Custom Offers
						</button>
					</div>

					{/* Call Information Card */}
					<div className="bg-white rounded-lg shadow-md p-6">
						<h2 className="text-xl font-semibold mb-4">Call Information</h2>
						<p className="text-gray-600 mb-4">
							Search and view detailed call information, transcripts, and recordings.
						</p>
						<button
							onClick={() => setCurrentView('callInformation')}
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
						>
							View Call Information
						</button>
					</div>

					{/* PMetrics Card */}
					<div className="bg-white rounded-lg shadow-md p-6">
						<h2 className="text-xl font-semibold mb-4">PMetrics Dashboard</h2>
						<p className="text-gray-600 mb-4">
							Access product metrics, churn analysis, and business insights.
						</p>
						<button
							onClick={() => setCurrentView('pMetrics')}
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
						>
							View PMetrics
						</button>
					</div>

					{/* PR Stats Card */}
					{/* <div className="bg-white rounded-lg shadow-md p-6">
						<h2 className="text-xl font-semibold mb-4">PR Stats</h2>
						<p className="text-gray-600 mb-4">Access PR stats and insights.</p>
						<button
							onClick={() => setCurrentView('prStats')}
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
						>
							View PR Stats
						</button>
					</div> */}

					{/* Grafana Dashboard Card */}
					<div className="bg-white rounded-lg shadow-md p-6">
						<h2 className="text-xl font-semibold mb-4">Grafana Dashboard</h2>
						<p className="text-gray-600 mb-4">Access monitoring and analytics dashboard.</p>

						{isGrafanaLoading ? (
							<div className="flex justify-center items-center h-28">
								<LoadingSpinner text="Loading credentials..." />
							</div>
						) : (
							<div className="mb-4 bg-gray-50 border rounded-md p-2">
								<div className="flex justify-between items-center mb-1">
									<span className="text-xs text-gray-500">User:</span>
									<button
										onClick={() => handleCopy(grafanaCreds.user, 'user')}
										className={`text-xs px-2 py-0.5 rounded transition-colors duration-200 ${
											copiedItem === 'user'
												? 'bg-green-200 text-green-800'
												: 'bg-gray-200 hover:bg-gray-300'
										}`}
									>
										{copiedItem === 'user' ? '✓ Copied' : 'Copy'}
									</button>
								</div>
								<p className="text-sm font-mono text-gray-800 mb-2">
									{grafanaCreds.user || 'Not available'}
								</p>

								<div className="flex justify-between items-center mb-1">
									<span className="text-xs text-gray-500">Password:</span>
									<button
										onClick={() => handleCopy(grafanaCreds.password, 'password')}
										className={`text-xs px-2 py-0.5 rounded transition-colors duration-200 ${
											copiedItem === 'password'
												? 'bg-green-200 text-green-800'
												: 'bg-gray-200 hover:bg-gray-300'
										}`}
									>
										{copiedItem === 'password' ? '✓ Copied' : 'Copy'}
									</button>
								</div>
								<p className="text-sm font-mono text-gray-800">••••••••</p>
							</div>
						)}
						<button
							onClick={() => window.open('https://grafana.myaifrontdeskadmin.com/login', '_blank')}
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
						>
							Open Grafana Dashboard
						</button>
					</div>

					{/* Clone User Card */}
					<div className="bg-white rounded-lg shadow-md p-6">
						<h2 className="text-xl font-semibold mb-4">Clone User to Dev Environment</h2>
						<p className="text-gray-600 mb-4">
							Clone a user account to the development environment with test payment methods. The
							cloned account will be automatically deleted after 2 days.
						</p>
						<form onSubmit={handleCloneUser} className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
								<input
									type="text"
									value={cloneUserForm.username}
									onChange={(e) => setCloneUserForm({ ...cloneUserForm, username: e.target.value })}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
									required
								/>
							</div>
							<button
								type="submit"
								disabled={isCloneUserLoading}
								className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
							>
								{isCloneUserLoading ? <LoadingSpinner text="Cloning..." /> : 'Clone User'}
							</button>
						</form>
					</div>

					<div className="bg-white rounded-lg shadow-md p-6">
						<h2 className="text-xl font-semibold mb-4">Phone Numbers</h2>
						<p className="text-gray-600 mb-4">View and manage available phone numbers</p>
						<button
							onClick={() => setCurrentView('phoneNumbers')}
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
						>
							View Phone Numbers
						</button>
					</div>

					{/* Feature Analytics Card */}
					<div className="bg-white rounded-lg shadow-md p-6">
						<h2 className="text-xl font-semibold mb-4">Feature Analytics</h2>
						<p className="text-gray-600 mb-4">
							View live dashboards for feature usage and user engagement.
						</p>
						<button
							onClick={() => setCurrentView('featureAnalytics')}
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
						>
							View Dashboards
						</button>
					</div>

					{/* Placeholder for future features */}
					<div className="bg-white rounded-lg shadow-md p-6">
						<h2 className="text-xl font-semibold mb-4">More Features</h2>
						<p className="text-gray-600 mb-4">Additional admin features coming soon.</p>
						<button
							disabled
							className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-400 bg-gray-200 cursor-not-allowed"
						>
							Coming Soon
						</button>
					</div>
				</div>
			</div>

			{/* Clone Success Popup */}
			{showCloneSuccess && cloneSuccessData && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
						<div className="flex justify-between items-center mb-4">
							<h2 className="text-xl font-semibold text-green-600">✅ User Cloned Successfully</h2>
							<button
								onClick={() => {
									setShowCloneSuccess(false);
									setCloneSuccessData(null);
								}}
								className="text-gray-400 hover:text-gray-600 text-xl font-bold"
							>
								×
							</button>
						</div>

						<div className="space-y-3 mb-6">
							<div className="bg-green-50 border border-green-200 rounded-md p-3">
								<p className="text-sm text-green-800">
									<strong>Cloned Username:</strong> {cloneSuccessData.clonedUsername}
								</p>
							</div>

							<div className="grid grid-cols-2 gap-3 text-sm">
								<div>
									<strong>Customer ID:</strong>
									<p className="text-gray-600 break-all">{cloneSuccessData.customerId}</p>
								</div>
								<div>
									<strong>Subscription ID:</strong>
									<p className="text-gray-600 break-all">{cloneSuccessData.subscriptionId}</p>
								</div>
							</div>

							<div className="text-sm">
								<strong>Agents Count:</strong> {cloneSuccessData.agentsCount}
							</div>

							<div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
								<p className="text-sm text-yellow-800">
									<strong>⚠️ Important:</strong> This cloned account will be automatically deleted
									in 2 days.
								</p>
								<p className="text-sm text-yellow-700 mt-1">
									<strong>Expires:</strong> {new Date(cloneSuccessData.expiresAt).toLocaleString()}
								</p>
							</div>
						</div>

						<div className="flex justify-end">
							<button
								onClick={() => {
									setShowCloneSuccess(false);
									setCloneSuccessData(null);
								}}
								className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default AdminDashboard;
